import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle2, AlertTriangle } from 'lucide-react';
import './demo.css';

/* ═══════════════════════════════════════════════════════
   TIMELINE — Hard cuts only, 3-4s per scene, loops
   ═══════════════════════════════════════════════════════ */
const SCENES = [
  { id: 1,  s: 0,    e: 3.5,  type: 'text', text: 'Your AI made thousands of decisions last week.' },
  { id: 2,  s: 3.5,  e: 7,    type: 'text', text: 'Can you prove what happened?' },
  { id: 3,  s: 7,    e: 11,   type: 'visual-blast', text: '143 decisions affected by one bad source.' },
  { id: 4,  s: 11,   e: 14.5, type: 'visual-resolved', text: 'Risk contained. PASS.' },
  { id: 5,  s: 14.5, e: 18,   type: 'text', text: 'Audit evidence generated automatically.' },
  { id: 6,  s: 18,   e: 21.5, type: 'text', text: 'No manual assembly.' },
  { id: 7,  s: 21.5, e: 25,   type: 'text', text: 'This is what regulators, auditors, and insurers will require.' },
  { id: 8,  s: 25,   e: 28.5, type: 'text', text: 'EU AI Act expands August 2026.' },
  { id: 9,  s: 28.5, e: 32,   type: 'text', text: 'Apex Trust proves what was true then — with a signed proof chain.' },
  { id: 10, s: 32,   e: 36,   type: 'text', text: 'First proof chain in 7 days' },
];

const LOOP_DURATION = 40; // 36s content + 4s hold on final frame

function useTimeline() {
  const [t, setT] = useState(0);
  const r = useRef({ start: null, raf: null });
  useEffect(() => {
    const state = r.current;
    state.start = performance.now();
    const tick = () => {
      const el = (performance.now() - state.start) / 1000;
      setT(el % LOOP_DURATION); // loop
      state.raf = requestAnimationFrame(tick);
    };
    state.raf = requestAnimationFrame(tick);
    return () => { if (state.raf) cancelAnimationFrame(state.raf); };
  }, []);
  return t;
}

/* ═══ Visual Mini Components ═══ */

function BlastRadiusVisual() {
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '70%', maxWidth: 800 }}>
        <div className="amber-pulse-box" style={{
          borderLeft: '4px solid #F59E0B',
          background: 'rgba(245,158,11,0.06)',
          borderRadius: '0 12px 12px 0',
          padding: '28px 36px',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={14} />
            Blast Radius
          </div>
          <div className="amber-pulse-text" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 24, color: '#F59E0B', lineHeight: 1.8 }}>
            <span style={{ fontSize: 40, fontWeight: 700 }}>143 decisions</span>{' '}
            share this upstream source
          </div>
        </div>
      </div>
    </div>
  );
}

function ResolvedVisual() {
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '70%', maxWidth: 800 }}>
        <div style={{
          borderLeft: '3px solid #10B981',
          background: 'rgba(16,185,129,0.1)',
          borderRadius: '0 12px 12px 0',
          padding: '20px 28px',
          display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 0 40px rgba(16,185,129,0.2)',
        }}>
          <Shield size={20} style={{ color: '#10B981', flexShrink: 0 }} />
          <span style={{ fontSize: 16, fontWeight: 600, color: '#10B981' }}>
            Risk eliminated · Detection to closure: 4 days · Controls restored: 9/9 · SOC 2 PI1 re-tested: PASS
          </span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════ */
export default function LinkedInDemo() {
  const t = useTimeline();

  /* Determine active scene */
  const activeScene = SCENES.find(sc => t >= sc.s && t < sc.e) || SCENES[SCENES.length - 1];
  const isInScene = t < SCENES[SCENES.length - 1].e;

  return (
    <div className="demo-root" data-testid="linkedin-demo">
      {/* All scenes rendered as hard cuts — only one visible at a time */}
      {SCENES.map((sc) => {
        const active = sc === activeScene && isInScene;
        /* For final frame hold after all scenes */
        const isFinalHold = !isInScene && sc.id === 10;

        if (!active && !isFinalHold) return null;

        if (sc.type === 'text') {
          return (
            <div key={sc.id} className="linkedin-scene" style={{ zIndex: sc.id }}>
              <p className="linkedin-text">{sc.text}</p>
            </div>
          );
        }

        if (sc.type === 'visual-blast') {
          return (
            <div key={sc.id} className="linkedin-scene" style={{ zIndex: sc.id }}>
              <BlastRadiusVisual />
              <div style={{ position: 'absolute', bottom: '12%', left: '50%', transform: 'translateX(-50%)', textAlign: 'center', zIndex: 10 }}>
                <p className="linkedin-text" style={{ fontSize: '2rem' }}>{sc.text}</p>
              </div>
            </div>
          );
        }

        if (sc.type === 'visual-resolved') {
          return (
            <div key={sc.id} className="linkedin-scene" style={{ zIndex: sc.id }}>
              <ResolvedVisual />
              <div style={{ position: 'absolute', bottom: '12%', left: '50%', transform: 'translateX(-50%)', textAlign: 'center', zIndex: 10 }}>
                <p className="linkedin-text" style={{ fontSize: '2rem' }}>{sc.text}</p>
              </div>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}
